import * as React from "react";

import { ArrowDownIcon, ArrowUpIcon } from "@/assets/icons";
import { cn } from "@/lib/utils";

import * as SelectPrimitive from "@radix-ui/react-select";

interface SelectContentProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> {
  showScrollButtons?: boolean;
}

const selectClasses = {
  trigger: {
    base: "flex h-10 w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 focus:ring-0",
    borders: "border-neutral-200 dark:border-neutral-800",
    states: "focus:outline-none",
    darkMode:
      "dark:bg-neutral-950 dark:ring-offset-neutral-950 dark:placeholder:text-neutral-400 dark:focus:ring-neutral-300",
    placeholder: "placeholder:text-neutral-500"
  },
  content: {
    base: "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-white text-neutral-950 shadow-md focus:ring-0",
    borders: "border-neutral-200 dark:border-neutral-800",
    darkMode: "dark:bg-neutral-950 dark:text-neutral-50",
    animation: {
      base: "data-[state=open]:animate-in data-[state=closed]:animate-out",
      fade: "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      zoom: "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
      slide:
        "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
    },
    position: {
      base: "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1"
    }
  },
  item: {
    base: "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:ring-0",
    states:
      "focus:bg-neutral-100 focus:text-neutral-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
    darkMode: "dark:focus:bg-neutral-800 dark:focus:text-neutral-50"
  },
  label: "py-1.5 pl-8 pr-2 text-sm font-semibold",
  separator: "-mx-1 my-1 h-px bg-neutral-100 dark:bg-neutral-800",
  scrollButton: "flex cursor-default items-center justify-center py-1"
};

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      selectClasses.trigger.base,
      selectClasses.trigger.borders,
      selectClasses.trigger.states,
      selectClasses.trigger.darkMode,
      selectClasses.trigger.placeholder,
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ArrowDownIcon className="fill-dim-gray h-4 min-w-4 ml-2" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(selectClasses.scrollButton, className)}
    {...props}
  >
    <ArrowUpIcon className="h-4 w-4 fill-txtBody" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(selectClasses.scrollButton, className)}
    {...props}
  >
    <ArrowDownIcon className="h-4 w-4 fill-txtBody" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  SelectContentProps
>(
  (
    {
      className,
      children,
      position = "popper",
      showScrollButtons = true,
      ...props
    },
    ref
  ) => (
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        selectClasses.content.base,
        selectClasses.content.borders,
        selectClasses.content.darkMode,
        selectClasses.content.animation.base,
        selectClasses.content.animation.fade,
        selectClasses.content.animation.zoom,
        selectClasses.content.animation.slide,
        position === "popper" && selectClasses.content.position.base,
        className
      )}
      position={position}
      {...props}
    >
      {showScrollButtons && <SelectScrollUpButton />}
      <SelectPrimitive.Viewport
        className={cn(
          "p-1",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      {showScrollButtons && <SelectScrollDownButton />}
    </SelectPrimitive.Content>
  )
);

SelectContent.displayName = "SelectContent";

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(selectClasses.label, className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      selectClasses.item.base,
      selectClasses.item.states,
      selectClasses.item.darkMode,
      className
    )}
    {...props}
  >
    {/* <span className="absolute left-2 flex h-3.5 items-center justify-center w-full">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span> */}

    <SelectPrimitive.ItemText className="w-full">
      {children}
    </SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn(selectClasses.separator, className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue
};
